import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import {
  ArrowLeft, Loader2, Building2, Mail, Tag,
  CheckCircle, XCircle, Eye, Clock, ShieldCheck, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiUrl } from '@/lib/api';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  VALUABLE: { label: 'Valuable', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: ShieldCheck },
  DELIVERED: { label: 'Delivered', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Eye },
  ACCEPTED: { label: 'Accepted', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle },
  REJECTED: { label: 'Rejected', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20', icon: XCircle },
};

export default function MerchantPitchDetailPage() {
  const { pitchId } = useParams<{ pitchId: string }>();
  const { token } = useAuth();
  const [pitch, setPitch] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<'accept' | 'reject' | null>(null);

  const fetchPitch = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl(`/api/v2/merchant/pitches/${pitchId}/view`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to load pitch (${res.status})`);
      }
      const data = await res.json();
      setPitch(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [pitchId, token]);

  useEffect(() => { fetchPitch(); }, [fetchPitch]);

  const handleAction = async (action: string) => {
    setActionLoading(action);
    setConfirmAction(null);
    try {
      const res = await fetch(getApiUrl(`/api/v2/merchant/pitches/${pitchId}/${action}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to ${action} pitch`);
      }
      const data = await res.json();
      setPitch(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <MerchantLayout>
        <div className="flex items-center justify-center py-20" data-testid="pitch-loading">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      </MerchantLayout>
    );
  }

  const pitchData = pitch as {
    product_name: string;
    category?: string;
    status_v2: string;
    vendor_name?: string;
    pitch_content: string;
    admin_notes?: string;
    effective_score?: number;
    created_at?: string;
    reviewed_at?: string;
    delivered_at?: string;
  } | null;

  if (error && !pitchData) {
    return (
      <MerchantLayout>
        <div className="text-center py-20" data-testid="pitch-error">
          <AlertTriangle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">{error}</p>
          <Link to="/merchant/recommendations" className="text-rose-400 hover:text-rose-300 font-medium">
            Back to recommendations
          </Link>
        </div>
      </MerchantLayout>
    );
  }

  const statusConf = STATUS_CONFIG[pitchData!.status_v2] || STATUS_CONFIG.DELIVERED;
  const StatusIcon = statusConf.icon;
  const canAct = pitchData!.status_v2 === 'DELIVERED';

  return (
    <MerchantLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="merchant-pitch-detail">
        {/* Back */}
        <Link to="/merchant/recommendations" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to recommendations
        </Link>

        {/* Header Card */}
        <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="pitch-title">{pitchData!.product_name}</h1>
              {pitchData!.category && (
                <span className="inline-flex items-center gap-1.5 text-zinc-500 mt-1 text-sm">
                  <Tag className="w-3.5 h-3.5" />
                  {pitchData!.category}
                </span>
              )}
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${statusConf.color}`} data-testid="pitch-status-badge">
              <StatusIcon className="w-4 h-4" />
              {statusConf.label}
            </span>
          </div>

          {/* Vendor Info */}
          {pitchData!.vendor_name && (
            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
              <div className="p-3 bg-white/5 rounded-lg">
                <Building2 className="w-5 h-5 text-zinc-400" />
              </div>
              <div>
                <p className="font-medium text-white">{pitchData!.vendor_name}</p>
              </div>
            </div>
          )}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left — Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Pitch Content */}
            <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
              <h2 className="text-base font-semibold text-white mb-4">Pitch Details</h2>
              <p className="text-zinc-300 whitespace-pre-wrap leading-relaxed" data-testid="pitch-content">
                {pitchData!.pitch_content}
              </p>
            </div>

            {/* Admin Notes */}
            {pitchData!.admin_notes && (
              <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-5">
                <p className="text-sm font-medium text-blue-300 mb-2">SalesWall Expert Notes</p>
                <p className="text-zinc-300 text-sm">{pitchData!.admin_notes}</p>
              </div>
            )}

            {/* Accept / Reject Action Bar */}
            <div className="bg-[#18181b] rounded-xl border border-white/5 p-6" data-testid="accept-reject-action-bar">
              {canAct ? (
                <>
                  {confirmAction ? (
                    <div className="space-y-4">
                      <p className="text-white font-medium">
                        {confirmAction === 'accept'
                          ? 'Accept this pitch? The vendor will be notified.'
                          : 'Reject this pitch? This cannot be undone.'}
                      </p>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleAction(confirmAction)}
                          disabled={!!actionLoading}
                          data-testid={`confirm-${confirmAction}-btn`}
                          className={confirmAction === 'accept'
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            : 'bg-rose-600 hover:bg-rose-700 text-white'}
                        >
                          {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          {confirmAction === 'accept' ? 'Confirm Accept' : 'Confirm Reject'}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => setConfirmAction(null)}
                          disabled={!!actionLoading}
                          className="text-zinc-400 hover:text-white hover:bg-white/5"
                          data-testid="cancel-action-btn"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={() => setConfirmAction('accept')}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11"
                        data-testid="accept-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept Pitch
                      </Button>
                      <Button
                        onClick={() => setConfirmAction('reject')}
                        variant="outline"
                        className="flex-1 border-rose-500/30 text-rose-400 hover:bg-rose-500/10 h-11"
                        data-testid="reject-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject Pitch
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <StatusIcon className={`w-5 h-5 ${pitchData!.status_v2 === 'ACCEPTED' ? 'text-emerald-400' : pitchData!.status_v2 === 'REJECTED' ? 'text-rose-400' : 'text-blue-400'}`} />
                  <p className="text-zinc-400">
                    {pitchData!.status_v2 === 'ACCEPTED' && 'You accepted this pitch.'}
                    {pitchData!.status_v2 === 'REJECTED' && 'You rejected this pitch.'}
                    {pitchData!.status_v2 === 'VALUABLE' && 'Loading engagement data...'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right — Meta & Score */}
          <div className="space-y-6">
            {/* Score Card */}
            {pitchData!.effective_score != null && (
              <div className="bg-[#18181b] rounded-xl border border-white/5 p-6 text-center">
                <p className="text-sm text-zinc-500 mb-2">SalesWall Score</p>
                <p className={`text-4xl font-bold ${pitchData!.effective_score >= 70 ? 'text-emerald-400' : pitchData!.effective_score >= 50 ? 'text-blue-400' : 'text-zinc-400'}`} data-testid="pitch-score">
                  {pitchData!.effective_score}
                </p>
                <p className="text-xs text-zinc-600 mt-1">out of 100</p>
              </div>
            )}

            {/* Metadata */}
            <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
              <h3 className="text-sm font-medium text-zinc-500 mb-4">Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Received</span>
                  <span className="text-white">{formatDate(pitchData!.created_at ?? '')}</span>
                </div>
                {pitchData!.reviewed_at && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Reviewed</span>
                    <span className="text-white">{formatDate(pitchData!.reviewed_at)}</span>
                  </div>
                )}
                {pitchData!.delivered_at && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Delivered</span>
                    <span className="text-white">{formatDate(pitchData!.delivered_at)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Error toast */}
        {error && pitchData && (
          <div className="fixed bottom-6 right-6 bg-rose-500/90 text-white px-4 py-3 rounded-lg shadow-lg" data-testid="error-toast">
            {error}
          </div>
        )}
      </div>
    </MerchantLayout>
  );
}
